import { shallow } from 'zustand/shallow';
import { ErrorBoundary } from 'react-error-boundary';
import { Grid, Box } from '@mui/material';
import { useStore } from '@store/GlobalStore';
import { QuotePanels } from '@pages/QuoteDetails/QuotePanels';
import { FallbackQuoteDetails } from '@components/errorBoundary/FallbackQuoteDetails';
import { QuoteStatus, TQuoteApiResponse } from '@models/quotes';
import { disabledQuotePanelStatuses } from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { CustomerSubmitButton } from '@pages/QuoteDetails/components/CustomerSubmitButton';
import { ProductOfferingId } from '@models/newQuote';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useDebouncedSidebarWidth } from '../../../hooks';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { WIDTH_CONSTANTS, BREAKPOINT_WIDTHS } from '@theme/widthConstants';
import { useQuoteStore } from '@store/Quote/QuoteStore';

export type QuotePanelsContainerProps = {
  isReadOnly: boolean;
  activeRating: {
    isActive: boolean;
    id: string | undefined;
  };
  quoteId: string;
  quote: TQuoteApiResponse;
};

// Panel style constants
const MIN_PANEL_WIDTH = 600;
const SIDEBAR_OFFSET = 52;
const PADDING_OFFSET = 48;
const BASE_PADDING = 5;
const MIN_PADDING = 0;
const MAX_PADDING = 4;

//  brekpoint responsive layout calculations
const useResponsiveLayout = (isSidebarOpen: boolean, sidebarWidth: number) => {
  const { isMobileOrSmallScreen } = useGetScreenSize();
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const getMaxWidthWhenClosed = () => {
    if (isMobileOrSmallScreen) return window.innerWidth;
    if (isXl) return BREAKPOINT_WIDTHS.xl;
    if (isLg) return BREAKPOINT_WIDTHS.lg;
    if (isMd) return BREAKPOINT_WIDTHS.md;
    return window.innerWidth;
  };

  const calculateAvailableWidth = () => {
    if (isMobileOrSmallScreen) return window.innerWidth;

    const sidebarOffset = isSidebarOpen
      ? sidebarWidth + SIDEBAR_OFFSET + PADDING_OFFSET
      : SIDEBAR_OFFSET + PADDING_OFFSET;

    return window.innerWidth - sidebarOffset;
  };

  const calculateResponsivePadding = () => {
    if (isMobileOrSmallScreen) return 0;

    const sidebarWidthValue = isSidebarOpen
      ? sidebarWidth + SIDEBAR_OFFSET + PADDING_OFFSET
      : SIDEBAR_OFFSET + PADDING_OFFSET;

    const viewportWidth = window.innerWidth;
    const actualWidth = Math.max(viewportWidth - sidebarWidthValue, MIN_PANEL_WIDTH);
    const widthPercentage = actualWidth / viewportWidth;

    let scaledPadding;
    if (widthPercentage < 0.4) {
      scaledPadding = MIN_PADDING;
    } else if (widthPercentage < 0.6) {
      scaledPadding = Math.max(MIN_PADDING, BASE_PADDING * widthPercentage);
    } else {
      scaledPadding = Math.max(MIN_PADDING, Math.min(MAX_PADDING, BASE_PADDING * widthPercentage * 1.5));
    }

    return Math.round(scaledPadding);
  };

  const availableWidth = calculateAvailableWidth();
  const maxWidthWhenClosed = getMaxWidthWhenClosed();
  const finalAvailableWidth = Math.max(Math.min(availableWidth, maxWidthWhenClosed), MIN_PANEL_WIDTH);
  const responsivePadding = calculateResponsivePadding();

  return {
    isMobileOrSmallScreen,
    finalAvailableWidth,
    responsivePadding,
  };
};

// Panel state management
const usePanelState = (quote: TQuoteApiResponse, isReadOnly: boolean, activeRating: { isActive: boolean }) => {
  const userAuth = useStore((state) => state.userAuth, shallow);
  const { isBroker, isLioSales, isCustomerUser } = userAuth;

  const arePanelsDisabled =
    isReadOnly ||
    disabledQuotePanelStatuses.includes(quote?.status.name as string) ||
    activeRating.isActive ||
    isLioSales ||
    (isBroker && quote?.status.name === QuoteStatus.Referred.queryParam);

  const showSecondarySubmit = isCustomerUser && quote?.offering?.id !== ProductOfferingId.SpecialEvent;

  return {
    arePanelsDisabled,
    showSecondarySubmit,
  };
};

export const QuotePanelsContainer = ({ quote, quoteId, isReadOnly, activeRating }: QuotePanelsContainerProps) => {
  const [panelInstances] = useQuoteStore((state) => [state.panelInstances], shallow);

  const { totalSidebarWidth: sidebarWidth, isSidebarOpen } = useDebouncedSidebarWidth();

  const { isMobileOrSmallScreen, finalAvailableWidth, responsivePadding } = useResponsiveLayout(
    isSidebarOpen,
    sidebarWidth,
  );

  const { arePanelsDisabled, showSecondarySubmit } = usePanelState(quote, isReadOnly, activeRating);

  const loading = !quote;

  const containerStyles = {
    position: 'relative' as const,
    marginTop: 5,
    overflow: 'visible',
    boxSizing: 'border-box' as const,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',

    ...(isMobileOrSmallScreen
      ? { maxWidth: '100vw' }
      : {
          maxWidth: `${finalAvailableWidth}px`,
          transition: 'max-width 0.2s',
        }),
  };

  const gridStyles = [
    {
      flexDirection: 'column' as const,
      padding: isMobileOrSmallScreen ? 1 : responsivePadding,
      paddingTop: 0,
      alignItems: isSidebarOpen ? ('flex-start' as const) : ('center' as const),
      width: '100%',
      boxSizing: 'border-box' as const,
      overflow: 'visible' as const,
      paddingRight: isMobileOrSmallScreen ? 1 : 0,
      paddingLeft: isMobileOrSmallScreen ? 1 : 0,
    },
    isMobileOrSmallScreen
      ? {
          margin: 'auto',
          maxWidth: '100vw',
        }
      : {
          margin: 'auto',
          justifyContent: 'center',
          display: 'flex',
          maxWidth: isSidebarOpen
            ? `${finalAvailableWidth}px`
            : { xl: WIDTH_CONSTANTS.xl, lg: WIDTH_CONSTANTS.lg, md: WIDTH_CONSTANTS.md },
          minWidth: `${MIN_PANEL_WIDTH}px`,
        },
  ];

  return (
    <Box sx={containerStyles}>
      <Grid data-testid='parent-grid' container spacing={isMobileOrSmallScreen ? 1 : 3} sx={gridStyles}>
        <ErrorBoundary FallbackComponent={FallbackQuoteDetails}>
          <QuotePanels isLoading={loading} quoteId={quoteId} disabled={arePanelsDisabled} />

          {showSecondarySubmit && !loading && (
            <Box sx={submitButtonStyles}>
              <CustomerSubmitButton variant='contained' quoteId={quoteId} panelInstances={panelInstances} />
            </Box>
          )}
        </ErrorBoundary>
      </Grid>
    </Box>
  );
};

// moving here for easier readability

const submitButtonStyles = {
  position: 'relative' as const,
  margin: 'auto',
  marginTop: 6,
  marginBottom: 1,
};
